import { useMemo, useState } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate, useRoute } from '@/ui/router';
import { CategoryChip, Dialog, EmptyState } from '@/ui/components/bits';
import { copyText } from '@/ui/clipboard';
import { exportSessionBlocks } from '@/import/export';
import { deleteSession } from '@/db/repo';

export function SessionScreen() {
  const route = useRoute();
  const id = route.params.get('id') ?? '';
  const { sessions, items, reload, toast } = useAppState();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const session = sessions.find((s) => s.id === id);
  const mine = useMemo(() => items.filter((i) => i.sessionId === id), [items, id]);

  const categories = useMemo(() => {
    const set = new Set(mine.map((i) => i.category));
    return [...set].sort();
  }, [mine]);

  if (!session) {
    return (
      <EmptyState icon="🔍" title="Session not found">
        It may have been deleted.
      </EmptyState>
    );
  }

  const cards = mine.filter((i) => i.type === 'flashcard').length;
  const questions = mine.length - cards;
  const mode = session.kind === 'chapter' ? 'chapter' : 'class';

  async function copyBlock() {
    setBusy(true);
    try {
      const exported = await exportSessionBlocks(id);
      const ok = await copyText(exported.text);
      toast(
        ok
          ? exported.blocks.length > 1
            ? `Copied as ${exported.blocks.length} parts.`
            : 'Copied as an import block.'
          : 'Could not reach the clipboard.',
      );
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await deleteSession(id);
      await reload();
      toast(`Deleted ${session!.shortLabel}.`);
      navigate('/');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <div className="stack">
      <button className="btn btn--ghost btn--sm" onClick={() => navigate('/')} style={{ alignSelf: 'flex-start' }}>
        ‹ All sessions
      </button>

      <h1>{session.title}</h1>
      <p className="mono muted" style={{ margin: 0 }}>
        {session.shortLabel} · {cards} cards · {questions} questions
      </p>

      {categories.length > 0 ? (
        <div className="row">
          {categories.map((c) => (
            <CategoryChip key={c} category={c} />
          ))}
        </div>
      ) : null}

      <div className="grid-2">
        <button
          className="mode-card"
          disabled={cards === 0}
          onClick={() => navigate('/study', { mode, session: id })}
        >
          <span className="mode-card__title">Flashcards</span>
          <span className="mode-card__sub">{cards} cards</span>
        </button>
        <button
          className="mode-card"
          disabled={questions === 0}
          onClick={() => navigate('/quiz', { mode, session: id })}
        >
          <span className="mode-card__title">Quiz</span>
          <span className="mode-card__sub">{questions} questions</span>
        </button>
      </div>

      <h2 className="section-title">Manage</h2>
      <div className="stack">
        <button className="btn btn--block" disabled={busy} onClick={() => void copyBlock()}>
          Copy as import block
        </button>
        <span className="faint">
          Puts this whole session on the clipboard in import format, split into parts if it is
          large. Paste it into a chapter-review chat.
        </span>
        <button className="btn btn--block btn--danger-text" disabled={busy} onClick={() => setConfirming(true)}>
          Delete session
        </button>
      </div>

      {confirming ? (
        <Dialog title="Delete this session?" onClose={() => setConfirming(false)}>
          <p style={{ margin: 0 }}>
            {session.title} and its {mine.length} item{mine.length === 1 ? '' : 's'} will be
            removed from every mode. This also removes them on your other device the next time it
            syncs.
          </p>
          <div className="row">
            <button className="btn btn--bad" disabled={busy} onClick={() => void remove()}>
              Delete
            </button>
            <button className="btn" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
